<template>
  <div class="stripe-connected-account-form-page">
    <div
      v-if="model"
      class="app-content-page app-content-page--transparent stripe-connected-account-form-page__header"
    >
      <app-back-button
        v-if="enableBackButton"
        class="stripe-connected-account-form-page__header__back"
        :i18n-code="routeBack.i18nCode"
        :route-name="routeBack.name"
        :route-params="routeBack.params"
      />

      <h1 class="app-content-title stripe-connected-account-form-page__header__title">
        <app-i18n :code="`entities.stripeConnectedAccount.form.title.${model[fields.businessType.name]}`" />
      </h1>

      <!-- <el-radio-group
        class="stripe-connected-account-form-page__header__radio"
        v-model="model[fields.businessType.name]"
        size="small"
      >
        <el-radio-button
          v-for="option in fields.businessType.options"
          :key="option.id"
          :label="option.id"
        >
          <i :class="option.id === 'company' ? 'icon-home' : 'icon-player'" />
          <span v-text="option.label" />
        </el-radio-button>
      </el-radio-group> -->
    </div>

    <div class="app-content-page">
      <el-form
        v-if="model"
        :model="model"
        :rules="rules"
        class="form"
        ref="form"
        v-loading="findLoading || saveLoading"
        @submit.native.prevent="doSubmit"
        @validate="onFieldValidation"
      >
        <el-steps
          v-if="!isEditing"
          :active="activeStepIndex"
          align-center
          finish-status="success"
        >
          <el-step :title="i18n('entities.stripeConnectedAccount.form.subtitle.information')" />
          <el-step :title="i18n('entities.stripeConnectedAccount.form.subtitle.verification')" />
        </el-steps>

        <!-- Step 1 -->
        <template v-if="isEditing || activeStepIndex === 0">
          <h2 class="app-content-subtitle">
            <app-i18n code="entities.stripeConnectedAccount.form.subtitle.information" />
          </h2>

          <el-row :gutter="16">
            <el-col :lg="8" :md="12" :sm="24">
              <el-form-item :label="fields.subpropertyName.label">
                <el-input
                  :value="subpropertyName"
                  prefix-icon="icon-cup"
                  disabled
                />
              </el-form-item>
            </el-col>

            <el-col :lg="8" :md="12" :sm="24">
              <app-field-provided
                v-if="isEditing"
                :label="fields.defaultCurrency.label"
                :caption="selectedCurrencyCaption"
              />
              <el-form-item
                v-else
                :label="fields.defaultCurrency.label"
                :prop="isEditing ? null : fields.defaultCurrency.name"
                :required="!isEditing && fields.defaultCurrency.required"
                :error="getFieldError(fields.defaultCurrency.name).message"
              >
                <app-currency-select
                  v-model="model[fields.defaultCurrency.name]"
                  :options="fields.defaultCurrency.options"
                  :country="model[fields.country.name]"
                  :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.defaultCurrency.name}`)"
                  :disabled="supportedCurrencies.length <= 1"
                  @input="$refs.form.validateField(fields.defaultCurrency.name)"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :lg="10" :md="12" :sm="24">
              <el-form-item
                :label="fields.email.label"
                :prop="fields.email.name"
                :required="fields.email.required"
                :error="getFieldError(fields.email.name).message"
              >
                <el-input
                  v-model="model[fields.email.name]"
                  :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.email.name}`)"
                  prefix-icon="icon-mail"
                />
              </el-form-item>
            </el-col>

            <el-col :lg="8" :md="12" :sm="24">
              <el-form-item
                :label="fields.phone.label"
                :prop="fields.phone.name"
                :required="fields.phone.required"
                :error="getFieldError(fields.phone.name).message"
              >
                <phone-input v-model="model[fields.phone.name]" />
              </el-form-item>
            </el-col>
          </el-row>

          <h2 class="app-content-subtitle">
            <app-i18n code="entities.stripeConnectedAccount.form.subtitle.address" />
          </h2>

          <app-address-form-partial
            ref="addressForm"
            field-name="address"
            :address.sync="model[fields.address.name]"
            :errors="getFieldError(fields.address.name)"
            @validate="onFieldValidation"
            disable-country
          />
        </template>

        <!-- Step 2 -->
        <template v-if="isEditing || activeStepIndex === 1">
          <h2 class="app-content-subtitle">
            <app-i18n code="entities.stripeConnectedAccount.form.subtitle.verification" />
          </h2>

          <div :key="`${model[fields.businessType.name]}`">
            <template v-if="isCompanyForm">
              <el-row :gutter="16">
                <el-col :lg="7" :md="8" :sm="24">
                  <app-field-provided
                    v-if="isEditing"
                    :label="fields.companyName.label"
                    :caption="model[fields.companyName.name]"
                  />
                  <el-form-item
                    v-else
                    :label="fields.companyName.label"
                    :prop="isEditing ? null : fields.companyName.name"
                    :error="getFieldError(fields.companyName.name).message"
                    :required="!isEditing"
                  >
                    <el-input
                      v-model="model[fields.companyName.name]"
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.companyName.name}`)"
                      prefix-icon="icon-home"
                      :disabled="isEditing"
                    />
                  </el-form-item>
                </el-col>

                <el-col :lg="isEditing ? 5 : 7" :md="8" :sm="24">
                  <app-field-provided
                    v-if="isEditing"
                    :label="fields.companyTaxId.label"
                  />
                  <el-form-item
                    v-else
                    :label="fields.companyTaxId.label"
                    :prop="record.taxIdProvided ? '' : fields.companyTaxId.name"
                    :error="getFieldError(fields.companyTaxId.name).message"
                    :required="!record.taxIdProvided"
                  >
                    <template v-if="record.taxIdProvided">
                      <el-input
                        disabled
                        :placeholder="i18n('common.provided')"
                        prefix-icon="icon-tick"
                      />
                    </template>
                    <template v-else>
                      <el-input
                        v-model="model[fields.companyTaxId.name]"
                        :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.companyTaxId.name}`)"
                        prefix-icon="icon-lock"
                      />
                    </template>
                  </el-form-item>
                </el-col>

                <el-col
                  :lg="10"
                  :md="12"
                  :sm="24"
                >
                  <el-form-item
                    v-if="enableMccField"
                    :label="fields.mcc.label"
                    :prop="fields.mcc.name"
                    :error="getFieldError(fields.mcc.name).message"
                    required
                  >
                    <el-select
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.mcc.name}`)"
                      v-model="model[fields.mcc.name]"
                    >
                      <i
                        slot="prefix"
                        class="icon-home"
                      />
                      <el-option
                        :key="option.id"
                        :label="option.label"
                        :value="option.id"
                        v-for="option in fields.mcc.options"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>

                <el-col :lg="24" :md="12" :sm="24">
                  <el-form-item
                    :label="fields.url.label"
                    :prop="fields.url.name"
                    :required="fields.url.required"
                    :error="getFieldError(fields.url.name).message"
                  >
                    <el-input
                      v-model="model[fields.url.name]"
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.url.name}`)"
                      prefix-icon="icon-link"
                    />
                  </el-form-item>
                </el-col>

                <el-col :lg="24" :md="24" :sm="24">
                  <el-form-item
                    :label="fields.productDescription.label"
                    :prop="fields.productDescription.name"
                    :error="getFieldError(fields.productDescription.name).message"
                    required
                  >
                    <el-input
                      v-model="model[fields.productDescription.name]"
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.productDescription.name}`)"
                      prefix-icon="icon-document"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
            </template>

            <template v-if="isIndividualForm">
              <el-row :gutter="16">
                <el-col :lg="12" :md="12" :sm="24">
                  <el-form-item
                    :label="fields.firstName.label"
                    :prop="fields.firstName.name"
                    :error="getFieldError(fields.firstName.name).message"
                    required
                  >
                    <el-input
                      v-model="model[fields.firstName.name]"
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.firstName.name}`)"
                      prefix-icon="icon-player"
                    />
                  </el-form-item>
                </el-col>

                <el-col :lg="12" :md="12" :sm="24">
                  <el-form-item
                    :label="fields.lastName.label"
                    :prop="fields.lastName.name"
                    :error="getFieldError(fields.lastName.name).message"
                    required
                  >
                    <el-input
                      v-model="model[fields.lastName.name]"
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.lastName.name}`)"
                      prefix-icon="icon-player"
                    />
                  </el-form-item>
                </el-col>

                <el-col :lg="12" :md="12" :sm="24">
                  <el-form-item
                    :label="fields.personalIdNumber.label"
                    :prop="fields.personalIdNumber.name"
                    :error="getFieldError(fields.personalIdNumber.name).message"
                    required
                  >
                    <el-input
                      v-model="model[fields.personalIdNumber.name]"
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.personalIdNumber.name}`)"
                      prefix-icon="icon-lock"
                    />
                  </el-form-item>
                </el-col>

                <el-col :lg="12" :md="12" :sm="24">
                  <el-form-item
                    :label="fields.birthdate.label"
                    :prop="fields.birthdate.name"
                    :error="getFieldError(fields.birthdate.name).message"
                    required
                  >
                    <el-date-picker
                      type="date"
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.birthdate.name}`)"
                      v-model="model[fields.birthdate.name]">
                    </el-date-picker>
                  </el-form-item>
                </el-col>
              </el-row>

              <div class="app-content-separator" />

              <el-row :gutter="16">
                <el-col :lg="12" :md="12" :sm="24">
                  <el-form-item
                    :prop="fields.verificationIdFront.name"
                    :required="fields.verificationIdFront.required"
                  >
                    <app-image-upload
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.verificationIdFront.name}`)"
                      :max="fields.verificationIdFront.max"
                      :schema="fields.verificationIdFront.fileSchema"
                      v-model="model[fields.verificationIdFront.name]"
                      stripe-upload
                    />
                  </el-form-item>
                </el-col>

                <el-col :lg="12" :md="12" :sm="24">
                  <el-form-item
                    :prop="fields.verificationIdBack.name"
                    :required="fields.verificationIdBack.required"
                  >
                    <app-image-upload
                      :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.verificationIdBack.name}`)"
                      :max="fields.verificationIdBack.max"
                      :schema="fields.verificationIdBack.fileSchema"
                      v-model="model[fields.verificationIdBack.name]"
                      stripe-upload
                    />
                  </el-form-item>
                </el-col>
              </el-row>
            </template>
          </div>

          <el-form-item
            :prop="isEditing ? null : fields.tosAccepted.name"
            :error="getFieldError(fields.tosAccepted.name).message"
            :required="!isEditing"
          >
            <el-checkbox
              v-model="model[fields.tosAccepted.name]"
              :disabled="isEditing"
            >
              <app-i18n code="entities.stripeConnectedAccount.policy" />
              <p
                v-if="record.tosAcceptance"
                class="stripe-connected-account-form-page__message"
                v-html="tosAcceptanceText"
              />
            </el-checkbox>
          </el-form-item>
        </template>

        <!-- Global error message -->
        <el-form-item
          v-if="globalErrorMessage"
          :error="globalErrorMessage"
        />

        <!-- Form controls -->
        <el-form-item>
          <div class="form-buttons form-buttons--sides">
            <!-- Back button -->
            <el-button
              :disabled="saveLoading"
              @click="doBack"
              type="secondary"
            >
              <app-i18n code="common.backButton" />
            </el-button>

            <!-- Create account button -->
            <el-button
              v-if="isEditing || activeStepIndex === 1"
              :disabled="saveLoading"
              @click="doSubmit"
              type="primary"
            >
              <app-i18n
                v-if="isEditing"
                code="entities.stripeConnectedAccount.update.title"
              />
              <app-i18n
                v-else
                code="entities.stripeConnectedAccount.create.title"
              />
            </el-button>

            <!-- Continue button -->
            <el-button
              v-else
              :disabled="saveLoading"
              @click="doContinue"
              type="primary"
            >
              <app-i18n code="common.continue" />
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';

import StripeFrontend from 'shared-stripe-services/dist/Frontend';

import { i18n } from '@shared/i18n';
import config from '@/config';

import { FormSchema } from '@shared/form/form-schema';
import { mapStripeFieldErrors, getStripeFieldNames } from '@shared/fields/helpers/mapStripeFields';

import { StripeConnectedAccountModel } from '@modules/stripe/stripe-connected-account/stripe-connected-account-model';
import { AddressModel } from '@shared/model/address-model';
import { isString } from 'lodash';

import supportedCurrencies from '@enums/supportedCurrencies.json';

const stripeFrontend = new StripeFrontend({
  publishableKey: config.stripeConfig.publishableKey,
  fileUploadEndpoint: config.stripeConfig.fileUploadEndpoint,
});

const { fields } = StripeConnectedAccountModel;
const { fields: addressFields } = AddressModel;

const formSchema = new FormSchema([
  fields.id,
  fields.subpropertyName,
  fields.phone,
  fields.country,
  fields.defaultCurrency,
  fields.email,
  fields.address,
  fields.businessType,
  fields.productDescription,
  fields.companyName,
  fields.companyTaxId,
  fields.mcc,
  fields.firstName,
  fields.lastName,
  fields.birthdate,
  fields.personalIdNumber,
  fields.tosAccepted,
  fields.url,
  fields.verificationIdFront,
  fields.verificationIdBack,
]);

export default {
  name: 'app-stripe-connected-account-form-page',

  props: ['id'],

  data() {
    return {
      rules: formSchema.rules(),
      model: {},
      tokenLoading: false,
      activeStepIndex: 0,
      i18n,
      prevRoute: null,
    };
  },

  computed: {
    ...mapGetters({
      record: 'stripeConnectedAccount/form/record',
      fieldErrors: 'stripeConnectedAccount/form/fieldErrors',
      globalErrorMessage: 'stripeConnectedAccount/form/globalErrorMessage',
      findLoading: 'stripeConnectedAccount/form/findLoading',
      saveLoading: 'stripeConnectedAccount/form/saveLoading',
      currentManage: 'auth/manage/currentManage',
      isRootScreen: 'layout/isRootScreen',
    }),

    isEditing() {
      return !!this.id;
    },

    fields() {
      return fields;
    },

    businessType() {
      const { model, fields } = this;
      if (!model) return null;
      return model[fields.businessType.name];
    },

    isCompanyForm() {
      return this.businessType === 'company';
    },

    isIndividualForm() {
      return this.businessType === 'individual';
    },

    supportedCurrencies() {
      const { country } = this.model;
      if (!country) return [];
      return this.fields.defaultCurrency.options.filter(({ id }) => {
        return supportedCurrencies[country].some(countryId => countryId === id);
      });
    },

    selectedCurrencyCaption() {
      const { supportedCurrencies, model, fields } = this;
      const selectedCurrency = supportedCurrencies.find(c => c.id === model[fields.defaultCurrency.name]);
      return selectedCurrency ? selectedCurrency.label : null;
    },

    stripeFieldsMap() {
      return {
        'email': {
          fields: [fields.email.name],
          stepIndex: 0,
        },
        'company[phone]': {
          fields: [fields.phone.name],
          stepIndex: 0,
        },
        'company[address][postal_code]': {
          fields: [`${fields.address.name}.${addressFields.postalCode.name}`],
          stepIndex: 0,
        },
        'company[address][country]': {
          fields: [`${fields.address.name}.${addressFields.country.name}`],
          stepIndex: 0,
        },
        'company[address][state]': {
          fields: [`${fields.address.name}.${addressFields.state.name}`],
          stepIndex: 0,
        },
        'business_profile[url]': {
          fields: [fields.url.name],
          stepIndex: 1,
        },
        'mcc': {
          fields: [fields.mcc.name],
          stepIndex: 1,
        },
        'company[tax_id]': {
          fields: [fields.companyTaxId.name],
          stepIndex: 1,
        },
      };
    },

    mappedFieldErrors() {
      try {
        const { tokenLoading, saveLoading, fieldErrors, stripeFieldsMap  } = this;
        if (tokenLoading || saveLoading) return {};
        return mapStripeFieldErrors({
          fieldErrors,
          stripeFieldsMap: stripeFieldsMap,
          modelName: 'StripeConnectedAccount',
          model: this.model,
        });
      } catch (e) {
        const message = i18n('common.unknownError');
        this.doSetStripeError({ message });
        return {};
      }
    },

    subpropertyName() {
      const { currentManage } = this;
      return `${currentManage.subproperty.name}`;
    },

    tosAcceptanceText() {
      const { record } = this
      if (!record || !record.tosAcceptance) return null

      const { ip, date } = record.tosAcceptance
      const dateText = moment(date).format('YYYY-MM-DD HH:mm')
      return i18n('entities.stripeConnectedAccount.fields.tosAcceptance')
        .replace('{{date}}', `<strong>${dateText}</strong>`)
        .replace('{{ip}}', `<strong>${ip}</strong>`)
    },

    enableBackButton() {
      return isString(this.routeBack.i18nCode) && this.routeBack.name;
    },

    routeBack() {
     if (this.prevRoute && this.prevRoute.name === 'finantialSettings') {
        return  {
          name: 'finantialSettings',
          params: {
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
          },
          i18nCode: 'subpropertySettings.menu',
        }
      } else {
        return  {
          name: 'home',
          params: null,
          i18nCode: 'home.nav.dashboard',
        }
      }
    },
    enableMccField({ model, fields }) {
      const country = model[fields.country.name]
      return ['US', 'CA', 'ES'].includes(country)
    },
  },

  created() {
    this.model = formSchema.initialValues(this.record);
  },

  async mounted() {
    if (this.isEditing) {
      await this.doFind({
        id: this.id,
        propertyId: this.currentManage.property.id,
      });

      this.model = formSchema.initialValues(this.record);

      try {
        await this.$nextTick();
        await this.doValidation();
      } catch (e) {
        // 
      }
    } else {
      await this.doNew();
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => vm.prevRoute = from)
  },

  methods: {
    ...mapActions({
      doFind: 'stripeConnectedAccount/form/doFind',
      doNew: 'stripeConnectedAccount/form/doNew',
      doUpdate: 'stripeConnectedAccount/form/doUpdate',
      doCreate: 'stripeConnectedAccount/form/doCreate',
      doHandleStripeError: 'stripeConnectedAccount/form/doHandleStripeError',
      doSetStripeError: 'stripeConnectedAccount/form/doSetStripeError',
      doClearStripeError: 'stripeConnectedAccount/form/doClearStripeError',
    }),

    doBack() {
      if (this.activeStepIndex === 0) {
        const { name, params } = this.routeBack
        const route = { name }
        if (params) route.params = params
        this.$router.push(route)
      } else {
        this.activeStepIndex--;
      }
    },

    async doContinue() {
      try {
        await this.doValidation();
        this.activeStepIndex++;

        if (!this.isEditing) return;

        await this.$nextTick();
        await this.doValidation();
      } catch (error) {
        return;
      }
    },

    async doValidation() {
      try {
        const { form, addressForm } = this.$refs;
        const forms = [];

        if (form) forms.push(form);
        if (addressForm) forms.push(addressForm.$refs.form);

        await Promise.all(forms.map(form => {
          form.clearValidate();
          return form.validate();
        }));
      } catch (error) {
        throw error;
      }
    },

    async doReset({ businessType = 'company' } = {}) {
      this.model = formSchema.initialValues(this.record);
      this.model.businessType = businessType;

      await this.$nextTick();
      this.$refs.form.clearValidate();
      
      if (this.$refs.addressForm) {
        this.$refs.addressForm.doReset();
        await this.$nextTick();
        this.$refs.addressForm.$refs.form.clearValidate();
      }
    },

    async doSubmit() {
      try {
        await this.doValidation();
      } catch (error) {
        return;
      }

      try {
        this.tokenLoading = true;

        const { id, ...data } = formSchema.cast(this.model);

        const { businessType } = data;
        const isCompany = businessType === 'company';

        const tokenResult = await this.generateStripeAccountToken(data);
        if ('error' in tokenResult) throw tokenResult.error;

        const { token: { id: accountToken } } = tokenResult;

        const values = {
          accountToken,
          businessType: data.businessType,
          country: data.country,
          email: data.email,
          url: data.url,
          ...isCompany && {
            mcc: data.mcc,
            productDescription: data.productDescription,
          },
          phone: data.phone,
        };

        const backToSettings = this.prevRoute && this.prevRoute.name === 'finantialSettings'

        if (this.isEditing) {
          return this.doUpdate({
            id,
            values,
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
            connectedAccountId: this.record.stripeConnectedAccountId,
            backToSettings,
          });
        } else {
          return this.doCreate({
            values,
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
            backToSettings,
          });
        }
      } catch (error) {
        this.doHandleStripeError(error);
      } finally {
        this.tokenLoading = false;
      }
    },

    async generateStripeAccountToken(data) {
      if (this.isCompanyForm) return stripeFrontend.createCompanyAccountToken({
        companyName: data.companyName,
        companyPhone: `${data.phone.prefix}${data.phone.number}`,
        companyTaxId: data.companyTaxId,
        companyAddressLine1: data.address.line1,
        companyAddressLine2: data.address.line2,
        companyAddressCity: data.address.city,
        companyAddressState: data.address.state,
        companyAddressPostalCode: data.address.postalCode,
        companyAddressCountry: data.address.country,
        tosShownAndAccepted: data.tosAccepted,
      });

      if (this.isIndividualForm) return stripeFrontend.createIndividualAccountToken({
        firstName: data.firstName,
        lastName: data.lastName,
        phone: `${data.phone.prefix}${data.phone.number}`,
        email: data.email,
        personalIdNumber: data.personalIdNumber,
        addressLine1: data.address.line1,
        addressLine2: data.address.line2,
        addressCity: data.address.city,
        addressState: data.address.state,
        addressPostalCode: data.address.postalCode,
        addressCountry: data.address.country,
        birthDateDay: data.birthdate.day,
        birthDateMonth: data.birthdate.month,
        birthDateYear: data.birthdate.year,
        verificationDocumentFrontToken: data.verificationIdFront[0].id,
        verificationDocumentBackToken: data.verificationIdBack[0].id,
        tosShownAndAccepted: data.tosAccepted,
      });
    },

    getFieldError(fieldName) {
      const mappedError = this.mappedFieldErrors[fieldName];
      if (!mappedError) return {};
      return mappedError;
    },

    findKeyRecursively(object, key) {
      let value;

      Object.keys(object).some(k => {
        if (k === key) {
          value = object[k];
          return true;
        }
        if (object[k] && typeof object[k] === 'object') {
          value = this.findKeyRecursively(object[k], key);
          return value !== undefined;
        }
      });

      return value;
    },

    onFieldValidation(formFieldName) {
      if (!this.globalErrorMessage) return;

      const { stripeFieldsMap } = this;
      const fieldNames = getStripeFieldNames({
        fieldName: formFieldName,
        stripeFieldsMap,
      });

      if (!fieldNames || !fieldNames.length) return;

      this.doClearStripeError({ fieldNames });
    },
  },
  watch: {
    businessType(businessType) {
      if (!businessType) return;
      this.doReset({ businessType });
      this.activeStepIndex = 0;
    },
    mappedFieldErrors: {
      handler(val) {
        if (!Object.keys(val).length) return;
        this.activeStepIndex = this.findKeyRecursively(val, 'stepIndex');
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.stripe-connected-account-form-page__header {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 16px auto;
  grid-template-areas:
    'back .'
    'title radio';
  grid-row-gap: 24px;
  align-items: center;
  margin-left: 8px;
}

.stripe-connected-account-form-page__header__back {
  grid-area: back;
}

.stripe-connected-account-form-page__header__radio {
  grid-area: radio;
}

.stripe-connected-account-form-page__header__title {
  grid-area: title;
  margin: 0;
}

.stripe-connected-account-form-page__message {
  margin: 0;
  color: #404040;
}
</style>
